import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';

import InfoIcon from '@/assets/icons/info-icon.svg?react';
import { useFormattedRates } from '@/hooks/useFormattedRates';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { Amount } from '@/types/amount';
import { Locale } from '@/types/locale';
import { ConversionDetails } from '@/types/transaction';
import { transactionFromToAmount } from '@/utility/transactionFromToAmount';

interface Props {
  readonly from: Amount;
  readonly to: Amount;
  readonly conversionDetails?: ConversionDetails | null;
}

const PERCENT_FORMATTER_CONFIG = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
  style: 'percent' as const,
};

export const AmountTransactionDetailsItem: React.FC<Props> = (props: Props): React.ReactElement => {
  const { from, to, conversionDetails } = props;
  const locale = React.useContext<Locale>(LocaleContext);
  const tr = useTranslator();

  const formattedRates = useFormattedRates(from, to, conversionDetails);
  const percentFormatter = useLocalizedFormatter(PERCENT_FORMATTER_CONFIG);

  const tooltipText: string | null =
    conversionDetails && conversionDetails.calizaRate > 0
      ? tr('CONVERSION_RATE_TOOLTIP', percentFormatter.format(conversionDetails.calizaRate / 100))
      : null;

  return (
    <Box>
      <Typography variant="body2">{transactionFromToAmount(locale, from, to)}</Typography>
      <Typography variant="body1" color="custom.LightGray">
        {tr('MID_MARKET_RATE')}: {formattedRates.market} (source: XE)
      </Typography>
      <Stack direction="row" color="custom.LightGray" gap={0.5} alignItems="center">
        <Typography variant="body1">
          {tr('FX_RATE')}: {formattedRates.client}
        </Typography>

        {tooltipText && (
          <Tooltip title={tooltipText} placement="top-start">
            <Box component="span" sx={styles.infoIcon}>
              <InfoIcon fill="currentColor" width={16} height={16} />
            </Box>
          </Tooltip>
        )}
      </Stack>
      {formattedRates.effective && (
        <Stack direction="row" color="custom.LightGray" gap={0.5} alignItems="center">
          <Typography key="vet-message" variant="body1">
            {`VET: ${formattedRates.effective}`}
          </Typography>
          <Tooltip title={tr('VET_TOOLTIP')} placement="bottom-start">
            <Box component="span" sx={styles.infoIcon}>
              <InfoIcon fill="currentColor" width={16} height={16} />
            </Box>
          </Tooltip>
        </Stack>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  infoIcon: {
    display: 'inline-flex',
    verticalAlign: 'middle',
  },
};
